import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { CloudArrowUp, CircleNotch, Calculator, Warning, CheckCircle, PlusCircle } from "@phosphor-icons/react";

const FORM_OPTIONS = [
  { v: "Schedule C", label: "Schedule C (sole prop / SMLLC)" },
  { v: "Schedule E", label: "Schedule E (rental)" },
  { v: "Schedule F", label: "Schedule F (farm)" },
];

const CATEGORY_CHOICES = [
  { v: "gross_receipts",    label: "Gross receipts" },
  { v: "returns_allowances", label: "Returns & allowances" },
  { v: "cogs",              label: "COGS" },
  { v: "advertising",       label: "Advertising" },
  { v: "car_truck",         label: "Car & truck" },
  { v: "commissions_fees",  label: "Commissions & fees" },
  { v: "contract_labor",    label: "Contract labor" },
  { v: "depreciation",      label: "Depreciation" },
  { v: "insurance",         label: "Insurance" },
  { v: "interest_mortgage", label: "Mortgage interest" },
  { v: "interest_other",    label: "Other interest" },
  { v: "legal_prof",        label: "Legal & professional" },
  { v: "office_expense",    label: "Office expense" },
  { v: "rent_lease_other",  label: "Rent / lease" },
  { v: "repairs",           label: "Repairs & maintenance" },
  { v: "supplies",          label: "Supplies" },
  { v: "taxes_licenses",    label: "Taxes & licenses" },
  { v: "travel",            label: "Travel" },
  { v: "meals",             label: "Meals (§274(n) 50%)" },
  { v: "utilities",         label: "Utilities" },
  { v: "wages",             label: "Wages" },
  { v: "fixed_asset",       label: "Fixed asset (§179 candidate)" },
  { v: "distributions",     label: "Owner draw / distribution (non-deductible)" },
  { v: "penalties",         label: "Penalty (§162(f) non-deductible)" },
];

/**
 * Trial-Balance Auto-Mapper — drop a QuickBooks/Excel TB and get canonical
 * MeF fields on the return, plus §274 add-backs and §179 candidates.
 */
export default function TBAutoMapPanel({ engagementId, clientId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [formType, setFormType] = useState("Schedule C");

  const { data: runs } = useQuery({
    queryKey: ["tb-runs", engagementId],
    queryFn: () => api.get(`/drake/tb/runs?engagement_id=${engagementId}`).then((r) => r.data),
    enabled: !!engagementId,
  });

  const { data: memory } = useQuery({
    queryKey: ["tb-memory", clientId],
    queryFn: () => api.get(`/drake/tb/memory?client_id=${clientId}`).then((r) => r.data),
    enabled: !!clientId,
  });

  const setMemory = useMutation({
    mutationFn: ({ account, category }) => api.put("/drake/tb/memory", { client_id: clientId, account, category }),
    onSuccess: (_r, vars) => {
      toast.success(`Remembered '${vars.account}' → ${vars.category}. Next TB upload will auto-route it.`);
      qc.invalidateQueries({ queryKey: ["tb-memory", clientId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const promoteAsset = useMutation({
    mutationFn: ({ tb_run_id, candidate_index, date_placed_in_svc }) =>
      api.post("/drake/tb/promote-asset", { tb_run_id, candidate_index, date_placed_in_svc }),
    onSuccess: (r) => {
      toast.success(`Added to Form 4562 #${r.data.form_index + 1} · §179 election $${r.data.cost_basis.toLocaleString()}`);
      qc.invalidateQueries({ queryKey: ["tb-runs", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const promoteAssetsBulk = useMutation({
    mutationFn: (tb_run_id) => api.post("/drake/tb/promote-assets-bulk", { tb_run_id }),
    onSuccess: (r) => {
      toast.success(`Promoted ${r.data.promoted} §179 candidate${r.data.promoted === 1 ? "" : "s"} to Form 4562 · $${r.data.total_cost.toLocaleString()} total cost`);
      qc.invalidateQueries({ queryKey: ["tb-runs", engagementId] });
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const upload = useMutation({
    mutationFn: () => {
      if (!file) throw new Error("Pick a Trial Balance file (CSV or Excel).");
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/drake/tb/upload?engagement_id=${engagementId}&form_type=${encodeURIComponent(formType)}`, fd);
    },
    onSuccess: (r) => {
      const d = r.data;
      const parts = [`${d.fields_written} lines mapped`];
      if (d.fixed_asset_candidates?.length) parts.push(`${d.fixed_asset_candidates.length} §179 candidate${d.fixed_asset_candidates.length === 1 ? "" : "s"}`);
      if (d.adjustments?.length) parts.push(`${d.adjustments.length} M-1 items`);
      if (d.unmapped?.length) parts.push(`${d.unmapped.length} unmapped`);
      toast.success(parts.join(" · "));
      qc.invalidateQueries({ queryKey: ["drake-fields", engagementId] });
      qc.invalidateQueries({ queryKey: ["tb-runs", engagementId] });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (e) => toast.error(errText(e)),
  });

  const latest = (runs || [])[0];

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition-shadow duration-200" data-testid="tb-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Calculator size={13} weight="fill" className="text-cyan-700" />
          Trial-Balance auto-map
        </h3>
        {latest && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="tb-latest-badge">
            {latest.fields_written} lines
          </span>
        )}
      </div>
      <p className="text-[10.5px] text-zinc-500 leading-snug mb-3">
        Drop a QuickBooks / Excel TB — accounts route to the right {formType.replace("Schedule ", "Sched ")} lines, meals halve at §274(n), fixed assets flag as §179 candidates.
      </p>

      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Target return</label>
      <select
        value={formType}
        onChange={(e) => setFormType(e.target.value)}
        className="w-full text-xs border border-zinc-200 rounded-md px-2 py-1.5 mb-2 bg-white"
        data-testid="tb-form-type"
      >
        {FORM_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>

      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Trial balance (CSV / TSV / XLSX)</label>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.tsv,.xlsx,.xlsm,.xls"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="w-full text-[11px] mb-2 file:mr-2 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:px-2 file:py-1 file:cursor-pointer"
        data-testid="tb-file-input"
      />
      <button
        data-testid="tb-upload-btn"
        disabled={!file || upload.isPending}
        onClick={() => upload.mutate()}
        className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-medium px-3 py-1.5 transition-colors duration-150"
      >
        {upload.isPending ? <><CircleNotch size={12} className="animate-spin" /> Mapping…</> : <><CloudArrowUp size={12} /> Map TB → {formType.replace("Schedule ", "Sched ")}</>}
      </button>

      {latest && (
        <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1.5" data-testid="tb-latest-summary">
          {latest.adjustments?.length > 0 && (
            <div className="rounded-sm bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid="tb-adjustments">
              <p className="text-[10.5px] font-semibold text-amber-900 mb-0.5 flex items-center gap-1">
                <Warning size={10} weight="fill" /> M-1 / Non-deductible ({latest.adjustments.length})
              </p>
              {latest.adjustments.slice(0, 5).map((a, i) => (
                <p key={`adj-${i}-${a.account}`} className="text-[10.5px] text-amber-900" data-testid={`tb-adj-${i}`}>
                  <span className="font-medium">{a.account}</span> · ${a.amount.toLocaleString()} · <span className="italic">{a.reason}</span>
                </p>
              ))}
            </div>
          )}
          {latest.fixed_asset_candidates?.length > 0 && (
            <div className="rounded-sm bg-emerald-50 border border-emerald-200 px-2 py-1.5" data-testid="tb-fixed-assets">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10.5px] font-semibold text-emerald-900 flex items-center gap-1">
                  <CheckCircle size={10} weight="fill" /> §179 candidates ({latest.fixed_asset_candidates.length})
                </p>
                {latest.fixed_asset_candidates.some((c) => !c.promoted) && (
                  <button
                    data-testid="tb-fa-promote-all-btn"
                    disabled={promoteAssetsBulk.isPending}
                    onClick={() => promoteAssetsBulk.mutate(latest.id)}
                    className="inline-flex items-center gap-1 text-[9.5px] font-medium text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-1.5 py-0.5 transition-colors duration-150 disabled:opacity-50"
                    title="Turn every un-promoted candidate into a Form 4562 line with §179 election on full cost"
                  >
                    <PlusCircle size={9} weight="fill" /> Promote all ({latest.fixed_asset_candidates.filter((c) => !c.promoted).length})
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {latest.fixed_asset_candidates.map((f, i) => (
                  <div key={`fa-${i}-${f.asset_description}`} className="flex items-center gap-1.5" data-testid={`tb-fa-${i}`}>
                    <p className="flex-1 text-[10.5px] text-emerald-900 truncate">
                      <span className="font-medium">{f.asset_description}</span> · ${f.cost_basis.toLocaleString()}
                    </p>
                    {f.promoted ? (
                      <span className="inline-flex items-center gap-0.5 text-[9.5px] text-emerald-800 font-mono" data-testid={`tb-fa-promoted-${i}`}>
                        <CheckCircle size={9} weight="fill" /> 4562 #{(f.form_index ?? 0) + 1}
                      </span>
                    ) : (
                      <button
                        data-testid={`tb-fa-add-btn-${i}`}
                        disabled={promoteAsset.isPending}
                        onClick={() => promoteAsset.mutate({ tb_run_id: latest.id, candidate_index: i })}
                        className="inline-flex items-center gap-1 text-[9.5px] font-medium text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-1.5 py-0.5 transition-colors duration-150 disabled:opacity-50"
                        title="Create a Form 4562 line with §179 election = full cost"
                      >
                        <PlusCircle size={9} weight="fill" /> Add to 4562
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {latest.unmapped?.length > 0 && (
            <details className="rounded-sm bg-zinc-50 border border-zinc-200 px-2 py-1.5" data-testid="tb-unmapped" open>
              <summary className="cursor-pointer text-[10.5px] font-semibold text-slate-800">
                {latest.unmapped.length} unmapped row{latest.unmapped.length === 1 ? "" : "s"} — assign a category once, remembered forever
              </summary>
              <ul className="mt-1.5 space-y-1">
                {latest.unmapped.map((u, i) => {
                  const remembered = (memory?.overrides || {})[u.account.toLowerCase()];
                  const suggestions = (u.suggestions || []).slice(0, 3);
                  return (
                    <li key={`unmapped-${i}-${u.account}`} className="rounded-sm border border-zinc-200 bg-white px-1.5 py-1" data-testid={`tb-unmapped-${i}`}>
                      <div className="flex items-center gap-1.5">
                        <span className="flex-1 text-[10.5px] text-zinc-700 truncate">
                          {u.account} · <span className="font-mono text-zinc-500">${u.amount.toLocaleString()}</span>
                        </span>
                        <select
                          data-testid={`tb-remap-${i}`}
                          defaultValue={remembered || ""}
                          onChange={(e) => {
                            const cat = e.target.value;
                            if (cat) setMemory.mutate({ account: u.account, category: cat });
                          }}
                          className="text-[10.5px] border border-zinc-300 rounded-sm bg-white px-1 py-0.5 max-w-[140px]"
                        >
                          <option value="">More…</option>
                          {CATEGORY_CHOICES.map((c) => (
                            <option key={c.v} value={c.v}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      {suggestions.length > 0 && !remembered && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`tb-suggestions-${i}`}>
                          <span className="text-[9.5px] text-zinc-400 self-center">Suggested:</span>
                          {suggestions.map((s) => (
                            <button
                              key={s.category}
                              data-testid={`tb-suggest-${i}-${s.category}`}
                              onClick={() => setMemory.mutate({ account: u.account, category: s.category })}
                              className="text-[10px] font-medium rounded-sm border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 px-1.5 py-0.5 transition-colors duration-150"
                              title={`Route to ${s.label} (confidence ${s.score})`}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {remembered && (
                        <p className="mt-0.5 text-[9.5px] text-emerald-700" data-testid={`tb-remembered-${i}`}>
                          ✓ remembered as {CATEGORY_CHOICES.find((c) => c.v === remembered)?.label || remembered} — re-upload to apply
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
              {Object.keys(memory?.overrides || {}).length > 0 && (
                <p className="mt-1.5 text-[10px] text-zinc-500" data-testid="tb-memory-count">
                  {Object.keys(memory.overrides).length} account{Object.keys(memory.overrides).length === 1 ? "" : "s"} remembered — re-upload the TB to apply.
                </p>
              )}
            </details>
          )}
        </div>
      )}
    </div>
  );
}
